const API_URL: string = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(API_URL + '/api' + path, {
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text();
    }
    throw new ApiError('API ' + String(response.status) + ' on ' + path, response.status, body);
  }

  return (await response.json()) as T;
}

export interface ApiMenuItem {
  id: string;
  categoryId: string;
  sku: string;
  nameAr: string;
  nameEn: string;
  descriptionAr: string | null;
  descriptionEn: string | null;
  basePrice: number;
  currency: string;
  prepTimeSeconds: number;
  imageUrl: string | null;
  videoUrl: string | null;
  allergens: string[];
  dietaryTags: string[];
  isSignature: boolean;
}

export interface ApiMenuCategory {
  id: string;
  nameAr: string;
  nameEn: string;
  slug: string;
  sortOrder: number;
  items: ApiMenuItem[];
}

export interface CustomizeRequest {
  menuItemId: string;
  baseImageUrl: string;
  customizations: Record<string, string | number | boolean | null>;
  promptOverride?: string;
}

export interface CustomizeResponse {
  assetId: string;
  status: 'ready' | 'cached' | 'pending' | 'generating';
  imageUrl: string | null;
  cacheKey: string;
  fromCache: boolean;
  costUsd?: number;
}

export interface SommelierRecommendation {
  itemId: string;
  sku: string;
  nameAr: string;
  nameEn: string;
  basePrice: number;
  currency: string;
  score: number;
  reason: string;
}

export interface PlaceOrderItem {
  menuItemId: string;
  quantity: number;
  specialInstructions?: string;
}

export interface PlaceOrderRequest {
  branchId: string;
  tableId: string;
  customerId?: string;
  items: PlaceOrderItem[];
  notes?: string;
}

export interface OrderSummary {
  id: string;
  orderNumber: number;
  status: string;
  total: number;
  currency: string;
}

export interface LoyaltyAccount {
  userId: string;
  tierName: string;
  pointsBalance: number;
  lifetimePoints: number;
  streakDays: number;
  pointsToNextTier: number | null;
  multiplier: number;
}

export interface ArAssetResponse {
  menuItemId: string;
  status: 'ready' | 'cached' | 'pending' | 'generating' | 'failed';
  usdzUrl: string | null;
  fromCache: boolean;
  assetId: string;
}

export const api = {
  getMenu(branchId?: string): Promise<ApiMenuCategory[]> {
    const qs = branchId ? '?branchId=' + encodeURIComponent(branchId) : '';
    return request<ApiMenuCategory[]>('/menu' + qs);
  },

  customize(input: CustomizeRequest): Promise<CustomizeResponse> {
    return request<CustomizeResponse>('/ai/customize', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  sommelier(mood: string, branchId?: string): Promise<SommelierRecommendation[]> {
    return request<SommelierRecommendation[]>('/sommelier/recommend', {
      method: 'POST',
      body: JSON.stringify({ mood, branchId }),
    });
  },

  placeOrder(input: PlaceOrderRequest): Promise<OrderSummary> {
    return request<OrderSummary>('/orders', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  getLoyalty(userId: string): Promise<LoyaltyAccount> {
    return request<LoyaltyAccount>('/loyalty/' + encodeURIComponent(userId));
  },

  getArAsset(menuItemId: string): Promise<ArAssetResponse> {
    return request<ArAssetResponse>('/ar/items/' + encodeURIComponent(menuItemId));
  },

  requestArGeneration(menuItemId: string, baseImageUrl?: string): Promise<ArAssetResponse> {
    return request<ArAssetResponse>('/ar/generate', {
      method: 'POST',
      body: JSON.stringify({ menuItemId, baseImageUrl }),
    });
  },
};
